import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Box, Eye, EyeOff, ShieldCheck, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

export default function Login() {
  const { login } = useAuth()
  const navigate  = useNavigate()

  const [form, setForm]       = useState({ email: '', password: '', code: '' })
  const [showPw, setShowPw]   = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  // When the account has 2FA, the first submit flips this on and the
  // form swaps to an authentication-code prompt.
  const [twoFactor, setTwoFactor] = useState(false)

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await login(
        form.email,
        form.password,
        twoFactor ? form.code : undefined,
      )
      if (result?.twoFactorRequired) {
        setTwoFactor(true)
        return
      }
      if (result?.requiresVerification) {
        navigate('/verify-otp', {
          state: {
            email:  result.email || form.email,
            devOtp: result.devOtp,
          },
        })
        return
      }
      navigate('/dashboard')
    } catch (err) {
      setError(
        err.response?.data?.errors?.code?.[0] ||
        err.response?.data?.message ||
        'Invalid email or password.'
      )
    } finally {
      setLoading(false)
    }
  }

  const backToPassword = () => {
    setTwoFactor(false)
    setError('')
    setForm(f => ({ ...f, code: '' }))
  }

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-white/5">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Go to Stovalt home">
          <div className="w-9 h-9 bg-brand-500 rounded-xl flex items-center justify-center">
            <Box size={18} className="text-white" />
          </div>
          <span className="font-display font-700 text-white text-xl">Stovalt</span>
        </Link>

        <div>
          <h2 className="font-display text-4xl font-700 text-white leading-tight mb-4">
            Storage management<br />
            <span className="text-brand-400">built for operators.</span>
          </h2>
          <p className="text-slate-400 text-base leading-relaxed max-w-sm">
            Run your facility from one place - units, tenants, invoices, and access codes, all in one clean dashboard.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-6">
            {[
              { label: 'Units managed', value: '50K+' },
              { label: 'Avg. occupancy', value: '91%' },
              { label: 'Operators', value: '1,200+' },
            ].map(s => (
              <div key={s.label}>
                <p className="text-2xl font-display font-700 text-brand-400">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-600">© {new Date().getFullYear()} Stovalt. All rights reserved.</p>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity" aria-label="Go to Stovalt home">
            <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
              <Box size={16} className="text-white" />
            </div>
            <span className="font-display font-700 text-white text-lg">Stovalt</span>
          </Link>

          {twoFactor ? (
            <>
              <div className="w-10 h-10 bg-brand-500/10 rounded-xl flex items-center justify-center mb-4">
                <ShieldCheck size={20} className="text-brand-400" />
              </div>
              <h1 className="font-display text-2xl font-700 text-white mb-1">Two-factor authentication</h1>
              <p className="text-slate-400 text-sm mb-8">
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-700 text-white mb-1">Welcome back</h1>
              <p className="text-slate-400 text-sm mb-8">Sign in to your operator account</p>
            </>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {twoFactor ? (
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  Authentication code
                </label>
                <input
                  type="text"
                  inputMode="text"
                  autoFocus
                  value={form.code}
                  onChange={set('code')}
                  required
                  placeholder="123456"
                  className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    required
                    placeholder="you@yourfacility.com"
                    className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={set('password')}
                      required
                      placeholder="••••••••"
                      className="block w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {!twoFactor && (
              <div className="flex justify-end -mt-1">
                <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-brand-400">
                  Forgot password?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : twoFactor ? 'Verify' : 'Sign in'
              }
            </button>
          </form>

          {twoFactor ? (
            <button
              onClick={backToPassword}
              className="mt-6 mx-auto flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300"
            >
              <ArrowLeft size={14} /> Back to sign in
            </button>
          ) : (
            <p className="text-center text-sm text-slate-500 mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-brand-400 hover:text-brand-300 font-medium">
                Create one free
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
